import * as T from 'three'
import {disposePreviewModel} from './dispose-preview-model'
import {RoundedBoxGeometry} from 'three/addons/geometries/RoundedBoxGeometry.js'
import {createCastleModel} from './castle-model'
import type {BeddingId,BeddingSelection} from '../../../config/blissBedding'
import {createErnstModel} from './ernst-model'
import type {PreviewLayout} from './ernst-layout'
import {addPhotoUpgrades} from './photo-upgrade-model'
import type {BasicUpgrades} from '../../../../shared/basicUpgrades'
import {createBunkModel} from '../design/configurator/room/bunk-model'
import {bunkJoinery} from '../design/configurator/room/bunk-joinery'
import {BED_OPTIONS,type Bed} from '../design/configurator/room/beds'
import {INITIAL_ROOM} from '../design/configurator/room/model'

// Planner geometry is in feet; USDZ uses meters. Do not resize models to fit a room.
export function buildCameraBunk(index:number,upgrades:BasicUpgrades={},ceilingInches=96,layout?:PreviewLayout,ladderFlipped=false,bedding?:BeddingId,beddingOptions?:BeddingSelection) {
 const option=BED_OPTIONS[index];if(!option)throw new Error('Choose a bunk first.')
 const bed:Bed={...option,id:'camera-preview',x:0,y:0,height:80,rotation:0}
 const model=layout==='castle'?createCastleModel():layout==='ernst'?createErnstModel():createBunkModel(bed,INITIAL_ROOM,2,!!upgrades.bedDrawers,'horizontal',upgrades.bedDrawerFlat?'flat_pull':'shaker',upgrades.bedDrawerFlat?'cutout':'silver_bar',undefined,undefined,'flat',bedding?{upper:bedding,lower:bedding,...beddingOptions}:undefined)
 if(upgrades.bedDrawers&&layout!=='ernst'&&layout!=='castle'){
  const joinery=bunkJoinery(bed);
  model.traverse(o=>{if(o.name==='drawer-face-flat_pull'||o.name==='drawer-face-shaker')o.position.x=(joinery.lower.x+joinery.lower.width-joinery.thickness-.75-bed.width/2)/12;});
 }
 // Move the complete entry assembly to the opposite end. The uninterrupted
 // head rails move into the old opening; the ladder, opening and supports stay together.
 if(ladderFlipped&&layout!=='ernst'&&layout!=='castle'&&!upgrades.stairs){
  const mirror=(bed.depth-3-bed.depth)/12;
  model.traverse(o=>{if(o.name.startsWith('ladder-')||o.name.startsWith('entry-support-')||/^guard-\d-(head|foot-)/.test(o.name))o.position.z=mirror-o.position.z;});
 }
 // Original and custom-layout pillows need the same interaction identity as styled bedding.
 let pillowIndex=0;
 model.traverse(object=>{
  if(!(object instanceof T.Mesh)||!object.name.includes('pillow')||object.userData.pillowKey)return;
  const key=`original:${pillowIndex++}`;object.userData.pillowKey=key;
  object.scale.x=[.85,1,1.12][(beddingOptions?.pillowWidths?.[key]??1)%3];
 });
 const materials=new Set<T.Material>()
 model.traverse(object=>{
  let parent:T.Object3D|null=object;while(parent){if(parent.userData.bedding)return;parent=parent.parent;}
  if(object instanceof T.Line){object.visible=false;return}
  if(!(object instanceof T.Mesh))return
  object.castShadow=true;object.receiveShadow=true;
  const originals=Array.isArray(object.material)?object.material:[object.material]
  object.material=originals.map(material=>{
   materials.add(material);
   const hardware=object.name.startsWith('pull-');
   const color=hardware?0x777b80:object.userData.finishPart?0xffffff:('color' in material?(material as T.MeshStandardMaterial).color:0xfffdf8)
   const finishColor=new T.Color(color);if(object.userData.finishShade)finishColor.multiplyScalar(object.userData.finishShade);
   // Keep the original surface maps and finish response in the room preview.
   const surface=material instanceof T.MeshStandardMaterial?material.clone():new T.MeshStandardMaterial({side:material.side});
   surface.color.copy(finishColor);
   // Give painted millwork the same satin response across Basic and Custom models.
   if(object.userData.finishPart){surface.roughness=.5;surface.metalness=0;}
   if(hardware){surface.roughness=.3;surface.metalness=.65;}
   if(object.name==='compartment-sconce'){surface.emissive.setHex(0xffcf8b);surface.emissiveIntensity=.8;}
   return surface;
  });if(object.material.length===1)object.material=object.material[0]
 })
 materials.forEach(m=>m.dispose());
 const group=new T.Group();group.add(model);group.scale.setScalar(.3048);group.updateMatrixWorld(true)
 const box=new T.Box3().setFromObject(group),center=box.getCenter(new T.Vector3())
 model.userData.ceilingInches=Math.max(84,Math.min(240,ceilingInches))
 if(layout!=='ernst'&&layout!=='castle')addPhotoUpgrades(model,bed,upgrades,model.userData.ceilingInches)
 // Apply detail quality to every model, including bedding and added accessories.
 // Small real bevels catch the shared room light without drawn outlines or bright spots.
 model.traverse(object=>{
  if(!(object instanceof T.Mesh))return;
  object.castShadow=true;object.receiveShadow=true;
  if(object.userData.finishPart&&object.geometry instanceof T.BoxGeometry){
   const original=object.geometry,{width,height,depth}=original.parameters;
   original.computeBoundingBox();
   const center=original.boundingBox!.getCenter(new T.Vector3());
   object.geometry=new RoundedBoxGeometry(width,height,depth,2,Math.min(.015,Math.min(width,height,depth)*.15));
   object.geometry.translate(center.x,center.y,center.z);
   original.dispose();
  }
 });
 group.position.set(-center.x,-box.min.y,-center.z)
 const scene=new T.Scene();scene.add(group);scene.updateMatrixWorld(true)
 scene.userData.ceilingRoot=model
 scene.userData.referenceBounds=box.clone().translate(group.position)
 return scene
}
export const disposeCameraBunk=disposePreviewModel;




